import { useState, useCallback } from 'react';
import { parseYupErrors } from './utils';
import {
  FieldErrors,
  FixedConcession,
  ProvisionalPromotionFormValues
} from '../types/ProvisionalPromotionTypes';
import { SnackbarSeverityType } from '../../../enums';
import studentPromotionApis from '../../../apis/studentPromotion';
import { provisionalPromotionSchema } from './validationSchema';

const usePromotionDialogForm = (
  selectedIds: string[],
  setToast: (toast: {
    open: boolean;
    message: string;
    severity: SnackbarSeverityType;
  }) => void
) => {
  const defaultAcademicYear = '2026-2027';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState<ProvisionalPromotionFormValues>({
    academicYear: defaultAcademicYear,
    grade: '',
    division: '',
    applicableConcessions: []
  });
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [fixedConcessions, setFixedConcessions] = useState<FixedConcession[]>(
    []
  );

  const openDialog = useCallback(() => {
    setFormValues({
      academicYear: defaultAcademicYear,
      grade: '',
      division: '',
      applicableConcessions: []
    });
    setFormErrors({});
    setDialogOpen(true);
  }, []);

  const closeDialog = () => setDialogOpen(false);

  const handleFieldChange = (name: string, value: string | string[]) => {
    setFormValues((prev) => ({ ...prev, [name]: value as any }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDialogSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await provisionalPromotionSchema.validate(formValues, {
        abortEarly: false
      });
      setFormErrors({});
      await studentPromotionApis.provisionalPromotion({
        studentIds: selectedIds,
        academicYear: formValues.academicYear,
        grade: formValues.grade,
        division: formValues.division,
        concessionsWaiversRemark: (formValues.applicableConcessions || []).join(
          '; '
        )
      });
      setToast({
        open: true,
        message: 'Provisionally promoted successfully',
        severity: SnackbarSeverityType.success
      });
      setDialogOpen(false);
    } catch (error: any) {
      const fieldErrors = parseYupErrors(error);
      if (Object.keys(fieldErrors).length) {
        setFormErrors(fieldErrors);
      } else {
        setToast({
          open: true,
          message: error?.message || 'Provisional Promotion failed',
          severity: SnackbarSeverityType.error
        });
      }
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line
  }, [selectedIds, formValues]);

  const fetchFixedConcessions = async (school: string) => {
    try {
      const response = await studentPromotionApis.getFixedConcessionsMaster(
        school
      );
      const concessions = Array.isArray(response) ? response : [];
      setFixedConcessions(concessions as FixedConcession[]);
    } catch (e) {
      console.error('Error fetching fixed concessions', e);
      setToast({
        open: true,
        message: 'Failed to load fixed concessions',
        severity: SnackbarSeverityType.error
      });
    }
  };

  return {
    dialogOpen,
    submitting,
    formValues,
    formErrors,
    fixedConcessions,
    openDialog,
    closeDialog,
    handleFieldChange,
    handleDialogSubmit,
    fetchFixedConcessions,
    defaultAcademicYear
  };
};

export default usePromotionDialogForm;
